import { Space, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import React, { forwardRef } from 'react';

import { useAppSelector } from 'shared/lib';
import { VitrinsOffersCard } from 'shared/ui/VitrinsOffersCard';

import type { BankItem } from 'entities/vitrins';
import { selectUniqVitrinsOffers } from 'entities/vitrins';

import { CONTROL_CARD_BUTTON_TEXT } from 'features/VitrinsOffers/config';
import { padSummaryToCount } from 'features/VitrinsOffers/lib/padSummaryToCount';

import { ControlCardButton } from './ControlCardButton';
import styles from './VitrinsOffersCards.module.scss';

type Props = {
  isGrayCards?: boolean;
  submitButtonText?: string;
  onOfferSubmit?: (offer: BankItem) => Promise<void>;
  summaryItemsCount?: number;
};

export const VitrinsOffersCards = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { isGrayCards, submitButtonText, onOfferSubmit, summaryItemsCount } = props;
  const offers = useAppSelector(selectUniqVitrinsOffers);
  const isMobile = useIsMobile();

  if (!offers.length) {
    return null;
  }

  const goTo = async (item: BankItem) => {
    await onOfferSubmit?.(item);
  };

  return (
    <Space
      direction="vertical"
      size={16}
      ref={ref}
    >
      {offers.map((offer) => (
        <VitrinsOffersCard
          isGrayCard={isGrayCards}
          key={offer.id + offer.subtitle}
          controls={[
            <ControlCardButton
              key={`button_${offer.id + offer.subtitle}`}
              onClick={() => goTo(offer)}
              buttonText={submitButtonText || CONTROL_CARD_BUTTON_TEXT.SEND_REQUEST}
            />,
          ]}
          {...offer}
          summary={padSummaryToCount(offer, summaryItemsCount, isMobile)}
          title={<Typography.Heading level={5}>{offer.title}</Typography.Heading>}
        >
          {offer.otherOffers?.map((offerChild) => {
            return (
              <VitrinsOffersCard
                className={styles.otherOfferCard}
                isGrayCard={isGrayCards}
                key={offerChild.id + offerChild.subtitle}
                controls={[
                  <ControlCardButton
                    key={`button_${offerChild.id + offerChild.subtitle}`}
                    onClick={() => goTo(offerChild)}
                    buttonText={submitButtonText || CONTROL_CARD_BUTTON_TEXT.SEND_REQUEST}
                  />,
                ]}
                {...offerChild}
                summary={padSummaryToCount(offerChild)}
                otherOffers={[]}
                title={<Typography.Heading level={5}>{offer.title}</Typography.Heading>}
              />
            );
          })}
        </VitrinsOffersCard>
      ))}
    </Space>
  );
});

VitrinsOffersCards.displayName = 'VitrinsOffersCards';
